import React, { Suspense, useEffect } from 'react';
import { useSpring, animated } from 'react-spring';
import { HStack } from 'native-base';
import { Box } from '@mui/material';
import styled from 'styled-components';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  useAppSelector,
  getProfilePanelState,
  getPublicUserProfilePanelState,
  getChatsPanelState,
  getChatRoomPanelState,
  getDevice,
} from '@breakroom-nx/redux';

import { theme } from '@breakroom-nx/assets';

export enum PanelType {
  Chat,
  Profile,
  OtherUserProfile,
}
export interface PanelWrapperProps {
  type: PanelType;
}

// Styled Components
const Pane = styled(Box)<{ $mobileDevice?: boolean }>`
  height: ${(props) => (props.$mobileDevice ? '100%' : '95vh')};
  position: fixed;
  top: 50%;
  background: ${theme.colors.surface[100]};
  border: 1px solid ${theme.colors.surface[200]};
`;

const ProfilePane = styled(Pane)<{ $mobileDevice?: boolean }>`
  right: -2vw;
  padding-right: 2vw;
  border-radius: ${(props) =>
    props.$mobileDevice ? '0px' : '25px 0px 0px 25px'};
  box-shadow: rgba(0, 0, 0, 0.25) -25px 0px 40px -12px;
`;

const ChatPane = styled(Pane)<{ $mobileDevice?: boolean }>`
  left: -2vw;
  padding-left: 2vw;
  border-radius: ${(props) =>
    props.$mobileDevice ? '0px' : '0px 25px 25px 0px'};
  box-shadow: rgba(0, 0, 0, 0.25) 25px 0px 40px -12px;
`;

// Platform Specific Components
const LazyLoadedChatPane = React.lazy(
  () => import('../chat-ui/chat-type-pane/chat-type-pane')
);
const LazyLoadedUserPane = React.lazy(
  () => import('../user/user-tab-pane/user-tab-pane')
);

const LazyLoadedChatRoomPane = React.lazy(
  () => import('../chat-ui/chat-room-pane/chat-room-pane')
);
const LazyLoadedUserContainer = React.lazy(
  () => import('../user/user-container/user-container')
);

function PanelWrapper({ type }: PanelWrapperProps) {
  const mobileDevice = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    // console.log('Rendering Panel Wrapper');
  });

  const getPaneVariant = () => {
    return type === PanelType.Chat ? ChatPane : ProfilePane;
  };

  const getSlideAmount = () => {
    return type === PanelType.Chat ? -100 : 100;
  };

  const AnimatedTypeSpecificPane = animated(getPaneVariant());

  // Panel Controls
  const paneDisplayed = useAppSelector(
    type === PanelType.Chat
      ? getChatsPanelState
      : type === PanelType.Profile
      ? getProfilePanelState
      : getPublicUserProfilePanelState
  );
  const chatRoomPaneDisplayed = useAppSelector(getChatRoomPanelState);

  // React Spring Animations
  const { transformPane, paneOpacity } = useSpring({
    paneOpacity: paneDisplayed ? 1 : 0,
    transformPane: `translateY(-50%) translateX(${
      paneDisplayed ? 0 : getSlideAmount()
    }vw)`,
    config: { mass: 5, tension: 500, friction: 80 },
  });

  const { transformSecondaryPane } = useSpring({
    transformSecondaryPane: `translateX(${chatRoomPaneDisplayed ? -100 : 0}%)`,
    config: { mass: 5, tension: 500, friction: 80 },
  });

  return (
    <AnimatedTypeSpecificPane
      width={{
        xs: '100vw',
        sm: '60vw',
        md: '40vw',
        lg: '30vw',
        xl: '20vw',
      }}
      style={{
        opacity: paneOpacity,
        transform: transformPane,
        overflow: 'hidden',
        display: 'flex',
        position: 'fixed',
        zIndex: 9999,
      }}
      $mobileDevice={mobileDevice}
    >
      <animated.div
        style={{
          transform:
            type === PanelType.Chat ? transformSecondaryPane : undefined,
          width: '100%',
        }}
      >
        <HStack style={{ width: '100%', height: '100%' }}>
          <div
            style={{
              width: `${
                type === PanelType.Chat && !chatRoomPaneDisplayed
                  ? '100%'
                  : '100%'
              }`,
            }}
          >
            {paneDisplayed && (
              <Suspense fallback="">
                {type === PanelType.Chat ? (
                  <LazyLoadedChatPane />
                ) : type === PanelType.Profile ? (
                  <LazyLoadedUserPane />
                ) : (
                  <LazyLoadedUserContainer />
                )}
              </Suspense>
            )}
          </div>
          <div style={{ width: '100%' }}>
            {type === PanelType.Chat && chatRoomPaneDisplayed && (
              <Suspense fallback="">
                <LazyLoadedChatRoomPane />
              </Suspense>
            )}
          </div>
        </HStack>
      </animated.div>
    </AnimatedTypeSpecificPane>
  );
}

export default React.memo(PanelWrapper);
